import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import apiService from '../services/api'
import logger from '../utils/logger'
import USER_CONFIG from '../config/user-config'

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Verificar token al inicializar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken')
        
        if (!token) {
          setIsAuthenticated(false)
          setLoading(false)
          return
        }

        // Verificar si es un token de desarrollo
        if (token.startsWith('dev-token-') || token.startsWith('demo-token-')) {
          setIsAuthenticated(true)
          setLoading(false)
          return
        }

        // Verificar token con el backend
        try {
          logger.auth('Verifying token with backend...')
          await apiService.getProfile()
          logger.auth('Token verification successful')
          setIsAuthenticated(true)
        } catch (error) {
          logger.error('Token inválido:', error)
          localStorage.removeItem('authToken')
          setIsAuthenticated(false)
        }
      } catch (error) {
        logger.error('Error verificando autenticación:', error)
        setIsAuthenticated(false)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  // Query para obtener perfil de usuario
  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken')
      logger.auth('Getting user profile, token:', token ? token.substring(0, 20) + '...' : 'null')
      
      // Si es un token de desarrollo, crear un usuario simulado
      if (token && (token.startsWith('dev-token-') || token.startsWith('demo-token-'))) {
        let devRole = 'cliente' // default
        
        // Detectar el rol del token de desarrollo
        logger.auth('Detecting role from dev token:', token)
        if (token.includes('-admin-')) {
          devRole = 'admin'
          logger.auth('Detected admin role')
        } else if (token.includes('-trabajador-')) {
          devRole = 'trabajador'
          logger.auth('Detected trabajador role')
        } else if (token.includes('-cliente-')) {
          devRole = 'cliente'
          logger.auth('Detected cliente role')
        } else {
          devRole = 'cliente'
          logger.auth('Using default cliente role')
        }
        
        logger.auth('Using dev token with role:', devRole)
        return {
          id: 1,
          email: USER_CONFIG.FORCE_EMAIL,  // Usar configuración forzada
          name: USER_CONFIG.FORCE_NAME,
          role: USER_CONFIG.FORCE_ROLE,
          is_active: true,
          company_id: USER_CONFIG.FORCE_COMPANY_ID
        }
      }
      
      // Si no es token de desarrollo, hacer la llamada al backend
      try {
        logger.auth('Calling backend for user profile...')
        const response = await apiService.getProfile()
        logger.debug('Backend response:', response.data)
        logger.debug('Backend response role:', response.data?.role)
        
        // Asegurar que el rol esté presente
        const userData = response.data || {}
        if (!userData.role) {
          logger.warn('No role found in backend response, using default: cliente')
          userData.role = 'cliente'
        }
        
        logger.debug('Final user data:', userData)
        return userData
      } catch (error) {
        logger.error('Error getting profile:', error)
        // Si falla, devolver un usuario por defecto
        return {
          id: 1,
          email: USER_CONFIG.FORCE_EMAIL,  // Usar configuración forzada
          name: USER_CONFIG.FORCE_NAME,
          role: USER_CONFIG.FORCE_ROLE,
          is_active: true,
          company_id: USER_CONFIG.FORCE_COMPANY_ID
        }
      }
    },
    enabled: isAuthenticated, // Removido !loading para que se ejecute inmediatamente
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
  })

  // Asegurar que siempre devolvemos un usuario válido
  const currentUser = user || (isAuthenticated ? {
    id: 1,
    email: USER_CONFIG.FORCE_EMAIL,  // Usar configuración forzada
    name: USER_CONFIG.FORCE_NAME,
    role: USER_CONFIG.FORCE_ROLE,
    is_active: true,
    company_id: USER_CONFIG.FORCE_COMPANY_ID
  } : null)

  // Debug: Log del estado del usuario
  logger.debug('Auth state:', { isAuthenticated, loading, userLoading, user, userError })
  logger.debug('Current user object:', user)
  logger.debug('Current user role:', user?.role)
  
  // Debug específico para el email
  console.log('🔍 DEBUG useAuth - User email:', user?.email)
  console.log('🔍 DEBUG useAuth - Current user email:', currentUser?.email)

  logger.debug('Final currentUser:', currentUser)

  // Función de login
  const login = (token) => {
    logger.auth('Login function called with token:', token ? token.substring(0, 20) + '...' : 'null')
    localStorage.setItem('authToken', token)
    setIsAuthenticated(true)
    queryClient.invalidateQueries(['user'])
    // Redirigir al dashboard después del login
    setTimeout(() => {
      logger.nav('Redirecting to dashboard...')
      navigate('/dashboard')
    }, 100)
  }

  // Función de login con token de desarrollo
  const loginWithDevToken = (token) => {
    localStorage.setItem('authToken', `dev-token-${token}`)
    setIsAuthenticated(true)
    queryClient.invalidateQueries(['user'])
    // Redirigir al dashboard después del login
    setTimeout(() => {
      navigate('/dashboard')
    }, 100)
  }
  
  // Función para forzar la recarga del usuario
  const refreshUser = () => {
    console.log('🔄 Forzando recarga del usuario...')
    queryClient.invalidateQueries(['user'])
    queryClient.refetchQueries(['user'])
  }

  // Función de login con token JWT real
  const loginWithJWT = (token) => {
    logger.auth('Login with JWT token:', token ? token.substring(0, 20) + '...' : 'null')
    localStorage.setItem('authToken', token)
    setIsAuthenticated(true)
    queryClient.invalidateQueries(['user'])
    // Redirigir al dashboard después del login
    setTimeout(() => {
      logger.nav('Redirecting to dashboard...')
      navigate('/dashboard')
    }, 100)
  }

  // Función de logout
  const logout = async () => {
    try {
      // Intentar hacer logout en el backend si no es un token de desarrollo
      const token = localStorage.getItem('authToken')
      if (token && !token.startsWith('dev-token-') && !token.startsWith('demo-token-')) {
        await apiService.logout()
      }
    } catch (error) {
      logger.error('Error en logout:', error)
    } finally {
      localStorage.removeItem('authToken')
      setIsAuthenticated(false)
      queryClient.clear()
      // Redirigir al login después del logout
      navigate('/login')
    }
  }

  // Función para verificar si el usuario está autenticado
  const checkAuth = () => {
    const token = localStorage.getItem('authToken')
    return !!token
  }

  return {
    isAuthenticated,
    loading: loading || userLoading,
    user: currentUser,
    login,
    loginWithDevToken,
    loginWithJWT,
    logout,
    checkAuth,
    refreshUser,
  }
}

export default useAuth